import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';
import { IS_PRODUCTION } from '@/config';

// ── Decorative SVG — graticule + Seine silhouette (left panel) ───────────────

/** Graticule mesh + stylised Seine river — purely decorative, no semantic content. */
function PanelDecor() {
  const hLines = [90, 180, 270, 360, 450, 540, 630, 720, 810];
  const vLines = [60, 120, 180, 240, 300, 360, 420, 480, 540];
  const nodes = hLines
    .filter((_, i) => i % 2 === 0)
    .flatMap((y) => vLines.filter((_, j) => j % 2 === 0).map((x) => ({ x, y })));

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox="0 0 600 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Graticule */}
      <g stroke="rgba(255,255,255,0.06)" strokeWidth="0.9" fill="none">
        {hLines.map((y) => (
          <line key={`h${y}`} x1={0} y1={y} x2={600} y2={y} />
        ))}
        {vLines.map((x) => (
          <line key={`v${x}`} x1={x} y1={0} x2={x} y2={900} />
        ))}
      </g>

      {/* Nœuds de croisement */}
      <g fill="rgba(255,255,255,0.1)">
        {nodes.map(({ x, y }) => (
          <circle key={`n${x}-${y}`} cx={x} cy={y} r={1.5} />
        ))}
      </g>

      {/* Seine — glow extérieur (halo bleuté) */}
      <path
        d="M -50 560 Q 70 505 180 568 T 390 508 T 650 590"
        fill="none"
        stroke="rgba(59,130,246,0.15)"
        strokeWidth="34"
        strokeLinecap="round"
      />
      {/* Seine — tracé principal */}
      <path
        d="M -50 560 Q 70 505 180 568 T 390 508 T 650 590"
        fill="none"
        stroke="rgba(147,197,253,0.20)"
        strokeWidth="10"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Terravision mark ─────────────────────────────────────────────────────────

/** Inline SVG mark: navy square + 3 contour lines + accent dot. */
function TerravisionMark({ size = 34 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="34" height="34" rx="7" fill="#1b2559" />
      <line x1="8" y1="11" x2="26" y2="11" stroke="rgba(255,255,255,0.88)" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="8" y1="17" x2="21" y2="17" stroke="rgba(255,255,255,0.62)" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="8" y1="23" x2="23" y2="23" stroke="rgba(255,255,255,0.44)" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="25.5" cy="23" r="2.5" fill="#3b82f6" />
    </svg>
  );
}

// ── ProConnect button ─────────────────────────────────────────────────────────

/** ProConnect SSO placeholder (non-functional in demo). Liseré BBR = tricolor top stripe. */
function ProConnectButton() {
  return (
    <button
      type="button"
      aria-label="S'identifier avec ProConnect — non disponible en démonstration"
      disabled
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        borderRadius: '11px',
        border: '1.5px solid #e4e8f0',
        padding: '11px 16px',
        background: '#ffffff',
        cursor: 'not-allowed',
        opacity: 0.65,
        overflow: 'hidden',
        fontFamily: "'Public Sans', system-ui, sans-serif",
      }}
    >
      {/* Liseré Bleu-Blanc-Rouge en haut */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(to right, #002395 33.33%, #fff 33.33%, #fff 66.66%, #ED2939 66.66%)',
        }}
      />
      {/* Pictogramme ProConnect simplifié */}
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="8" stroke="#002395" strokeWidth="1.5" fill="none" />
        <circle cx="9" cy="7" r="2.5" fill="#002395" />
        <path d="M4 15c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="#002395" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: '13px', fontWeight: 600, color: '#11193a' }}>
        S'identifier avec ProConnect
      </span>
    </button>
  );
}

// ── Stat item ────────────────────────────────────────────────────────────────

interface StatProps {
  value: string;
  label: string;
}

function Stat({ value, label }: StatProps) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'Libre Franklin', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: '32px',
          color: '#ffffff',
          lineHeight: 1,
          letterSpacing: '-0.5px',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "'Public Sans', system-ui, sans-serif",
          fontSize: '12px',
          color: 'rgba(174,182,212,0.85)',
          marginTop: '6px',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── LoginPage ─────────────────────────────────────────────────────────────────

const DEMO_EMAIL = 'demo@terravision.fr';
const DEMO_PASSWORD = 'demo1234';

const STATS: StatProps[] = IS_PRODUCTION ? [] : [
  { value: '280+', label: 'Communes accompagnées' },
  { value: '64', label: 'Indicateurs temps réel' },
  { value: 'J+1', label: 'Délai de mise à jour' },
];

export function LoginPage() {
  const [email, setEmail] = useState(IS_PRODUCTION ? '' : DEMO_EMAIL);
  const [password, setPassword] = useState(IS_PRODUCTION ? '' : DEMO_PASSWORD);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  // Déjà connecté → renvoyer vers le tableau de bord.
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', { email, password });
      login(data.access_token);
      navigate('/');
    } catch {
      setError('Identifiant ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}
    >
      {/* ── LEFT PANEL — navy institutionnel ─────────────────────────── */}
      <div
        style={{
          flex: '1.25',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '48px 52px',
          background:
            'radial-gradient(120% 120% at 18% 12%, #1c2750, #121a3a 45%, #0c1230)',
          overflow: 'hidden',
        }}
      >
        <PanelDecor />

        {/* Marque Terravision */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <TerravisionMark size={34} />
          <span
            style={{
              fontFamily: "'Libre Franklin', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: '15px',
              color: 'rgba(255,255,255,0.9)',
              letterSpacing: '-0.01em',
            }}
          >
            Terravision
          </span>
        </div>

        {/* Accroche + stats */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontFamily: "'Libre Franklin', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: '44px',
              lineHeight: 1.08,
              letterSpacing: '-1.3px',
              color: '#ffffff',
              margin: '0 0 40px 0',
              maxWidth: '480px',
            }}
          >
            Décider pour son territoire, sur preuve.
          </h1>

          <div style={{ display: 'flex', gap: '40px' }}>
            {STATS.map((s) => (
              <Stat key={s.value} {...s} />
            ))}
          </div>
        </div>

        {/* Crédit B.I.A */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <img
            src="/bia.png"
            alt="BUSINESS IA"
            style={{ height: '22px', width: 'auto', borderRadius: '5px', opacity: 0.95 }}
          />
          <span
            style={{
              fontFamily: "'Public Sans', system-ui, sans-serif",
              fontSize: '11px',
              color: 'rgba(139,147,169,0.8)',
              letterSpacing: '0.02em',
            }}
          >
            Une solution
          </span>
          <span
            style={{
              fontFamily: "'Libre Franklin', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: '11px',
              color: 'rgba(174,182,212,0.72)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Business IA
          </span>
          <span
            style={{
              display: 'inline-block',
              width: '16px',
              height: '1px',
              background: 'rgba(139,147,169,0.35)',
            }}
          />
          <span
            style={{
              fontFamily: "'Public Sans', system-ui, sans-serif",
              fontSize: '11px',
              color: 'rgba(139,147,169,0.6)',
            }}
          >
            businessia.fr
          </span>
        </div>
      </div>

      {/* ── RIGHT PANEL — white card ──────────────────────────────────── */}
      <div
        style={{
          flex: '0.85',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#eef1f6',
          padding: '40px 32px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '380px',
            background: '#ffffff',
            borderRadius: '16px',
            padding: '40px 36px',
            boxShadow: '0 8px 24px -16px rgba(17,25,58,.18)',
          }}
        >
          {/* En-tête carte */}
          <div style={{ marginBottom: '28px' }}>
            <h2
              style={{
                fontFamily: "'Libre Franklin', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: '20px',
                color: '#11193a',
                letterSpacing: '-0.3px',
                margin: '0 0 4px 0',
              }}
            >
              Connexion
            </h2>
            <p
              style={{
                fontFamily: "'Public Sans', system-ui, sans-serif",
                fontSize: '13px',
                color: '#6b7392',
                margin: 0,
              }}
            >
              Accédez à votre espace territorial
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Erreur */}
            {error && (
              <div
                role="alert"
                style={{
                  background: '#fdecec',
                  color: '#e1000f',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontFamily: "'Public Sans', system-ui, sans-serif",
                  fontWeight: 500,
                  marginBottom: '16px',
                }}
              >
                {error}
              </div>
            )}

            {/* Identifiant */}
            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="login-email"
                style={{
                  display: 'block',
                  fontFamily: "'Public Sans', system-ui, sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#41496a',
                  marginBottom: '6px',
                }}
              >
                Identifiant
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  borderRadius: '11px',
                  border: '1.5px solid #e4e8f0',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontFamily: "'Public Sans', system-ui, sans-serif",
                  color: '#11193a',
                  background: '#f6f8fb',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s ease, background 0.15s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#1b2559';
                  e.currentTarget.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e4e8f0';
                  e.currentTarget.style.background = '#f6f8fb';
                }}
              />
            </div>

            {/* Mot de passe */}
            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="login-password"
                style={{
                  display: 'block',
                  fontFamily: "'Public Sans', system-ui, sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#41496a',
                  marginBottom: '6px',
                }}
              >
                Mot de passe
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  borderRadius: '11px',
                  border: '1.5px solid #e4e8f0',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontFamily: "'Public Sans', system-ui, sans-serif",
                  color: '#11193a',
                  background: '#f6f8fb',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s ease, background 0.15s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#1b2559';
                  e.currentTarget.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e4e8f0';
                  e.currentTarget.style.background = '#f6f8fb';
                }}
              />
            </div>

            {/* Bouton Accéder */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#1b2559',
                color: '#ffffff',
                borderRadius: '11px',
                border: 'none',
                fontFamily: "'Public Sans', system-ui, sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.75 : 1,
                transition: 'background 0.15s ease, opacity 0.15s ease',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.background = '#2c3a78';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.background = '#1b2559';
              }}
            >
              {loading ? 'Connexion…' : 'Accéder'}
            </button>
          </form>

          {/* Séparateur */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '24px 0',
            }}
          >
            <div style={{ flex: 1, height: '1px', background: '#e4e8f0' }} />
            <span
              style={{
                fontFamily: "'Public Sans', system-ui, sans-serif",
                fontSize: '12px',
                color: '#8b93a9',
              }}
            >
              ou
            </span>
            <div style={{ flex: 1, height: '1px', background: '#e4e8f0' }} />
          </div>

          {/* ProConnect */}
          <ProConnectButton />

          {/* Note démo */}
          <p
            style={{
              marginTop: '20px',
              marginBottom: 0,
              fontFamily: "'Public Sans', system-ui, sans-serif",
              fontSize: '11px',
              color: '#8b93a9',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            Identifiants pré-remplis pour la démonstration
          </p>
        </div>
      </div>
    </div>
  );
}
